import React, { useState } from "react";
import { useMyDesignerPublisherRelationship, useUpdateMyDesignerPublisherNotes } from "@seabrookstudios/pitch2table";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Edit from "@mui/icons-material/Edit";
import Markdown from "react-markdown";
import { FullScreenDialog } from "../core/FullScreenDialog";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 * @param {string} props.notes
 */
const EditPublisherNotesDialog = ({ publisherId, notes }) => {
  const [draft, setDraft] = useState(notes);
  const updateNotes = useUpdateMyDesignerPublisherNotes(publisherId);

  const dirty = draft !== notes;

  const saveNotes = async () => {
    await updateNotes.mutateAsync({ notes: draft });
  };

  return (
    <FullScreenDialog
      title="Edit Notes"
      actionText="SAVE"
      canSave={!dirty || updateNotes.isPending}
      onSaveHandler={saveNotes}
      isBusy={updateNotes.isPending}
      Icon={() => <Edit />}
      size="small"
      onOpen={() => setDraft(notes)}
    >
      <TextField
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        fullWidth
        margin="dense"
        multiline
        minRows={8}
        autoFocus
        label="Notes"
        helperText="Markdown is supported."
      />
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const MyNotesWithPublisher = ({ publisherId }) => {
  const { data: relationship, isLoading } = useMyDesignerPublisherRelationship(publisherId);
  const notes = relationship?.notes ?? "";

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={1} square={false}>
      <CardHeader
        title="My Notes"
        action={<EditPublisherNotesDialog publisherId={publisherId} notes={notes} />}
      />
      <Box p={2} pt={0}>
        {notes ? (
          <Markdown>{notes}</Markdown>
        ) : (
          <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
            Add personal notes for this publisher. These notes are not shared.
          </Typography>
        )}
      </Box>
    </Card>
  );
};
